import type { DebugBridge } from './DebugBridge';

export interface DisassemblyLine {
  address: number;
  csr: number;
  bytes: string;
  mnemonic: string;
  operands: string;
  isCurrentPC: boolean;
  isBreakpoint: boolean;
}

export class DisassemblerView {
  readonly el: HTMLDivElement;
  private bridge: DebugBridge;
  private lines: DisassemblyLine[] = [];
  private currentAddress: number = 0;
  private currentCsr: number = 0;
  private followPCEnabled: boolean = false;
  private linesPerPage: number = 32;
  private gotoInput: HTMLInputElement;
  private gotoBtn: HTMLButtonElement;
  private followBtn: HTMLButtonElement;
  private linesEl: HTMLDivElement;
  private breakpoints: Set<string> = new Set();

  constructor(bridge: DebugBridge) {
    this.bridge = bridge;

    this.el = document.createElement('div');
    this.el.className = 'disasm-view';

    const toolbar = document.createElement('div');
    toolbar.className = 'disasm-toolbar';

    const addrLabel = document.createElement('span');
    addrLabel.textContent = 'Address:';
    toolbar.appendChild(addrLabel);

    this.gotoInput = document.createElement('input');
    this.gotoInput.type = 'text';
    this.gotoInput.placeholder = '0x000000';
    this.gotoInput.className = 'disasm-goto-input';
    this.gotoInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') this.handleGoto();
    });
    toolbar.appendChild(this.gotoInput);

    this.gotoBtn = document.createElement('button');
    this.gotoBtn.textContent = 'Go';
    this.gotoBtn.className = 'disasm-goto-btn';
    this.gotoBtn.onclick = () => this.handleGoto();
    toolbar.appendChild(this.gotoBtn);

    this.followBtn = document.createElement('button');
    this.followBtn.textContent = 'Follow PC';
    this.followBtn.className = 'disasm-follow-btn';
    this.followBtn.onclick = () => {
      this.followPCEnabled = !this.followPCEnabled;
      this.followBtn.classList.toggle('active', this.followPCEnabled);
    };
    toolbar.appendChild(this.followBtn);

    this.el.appendChild(toolbar);

    this.linesEl = document.createElement('div');
    this.linesEl.className = 'disasm-lines';
    this.el.appendChild(this.linesEl);
  }

  setBreakpoints(bps: Set<string>): void {
    this.breakpoints = bps;
  }

  private handleGoto(): void {
    let addr = parseInt(this.gotoInput.value.trim(), 16);
    if (isNaN(addr)) addr = 0;
    this.goToAddress(addr, this.currentCsr);
  }

  async disassembleRange(address: number, csr: number, count: number): Promise<DisassemblyLine[]> {
    const raw = await this.bridge.disassemble(csr, address, count);
    return raw.map(item => ({
      address: item.address,
      csr: item.csr,
      bytes: item.bytes,
      mnemonic: item.mnemonic,
      operands: item.operands,
      isCurrentPC: false,
      isBreakpoint: this.breakpoints.has(`${item.csr}:${item.address.toString(16).toUpperCase()}`),
    }));
  }

  async goToAddress(address: number, csr: number): Promise<void> {
    this.currentAddress = address;
    this.currentCsr = csr;
    this.lines = await this.disassembleRange(address, csr, this.linesPerPage);
    this.update();
  }

  async followPC(): Promise<void> {
    const regs = await this.bridge.getRegisters();
    const pc = regs.pc;
    const csr = regs.csr;
    if (this.followPCEnabled && (pc !== this.currentAddress || csr !== this.currentCsr)) {
      await this.goToAddress(pc, csr);
    }
  }

  async updatePC(pc: number, csr: number): Promise<void> {
    for (const line of this.lines) {
      line.isCurrentPC = (line.address === pc && line.csr === csr);
    }
    this.update();
  }

  render(): HTMLElement {
    this.update();
    return this.el;
  }

  update(): void {
    this.linesEl.innerHTML = '';

    for (const line of this.lines) {
      const row = document.createElement('div');
      row.className = 'disasm-line';

      if (line.isCurrentPC) {
        row.classList.add('disasm-pc');
      }
      if (line.isBreakpoint) {
        row.classList.add('disasm-bp');
      }

      const addrSpan = document.createElement('span');
      addrSpan.className = 'disasm-addr';
      addrSpan.textContent = line.address.toString(16).padStart(6, '0').toUpperCase();
      row.appendChild(addrSpan);

      const bytesSpan = document.createElement('span');
      bytesSpan.className = 'disasm-bytes';
      bytesSpan.textContent = line.bytes;
      row.appendChild(bytesSpan);

      const instSpan = document.createElement('span');
      instSpan.className = 'disasm-inst';
      instSpan.textContent = line.mnemonic + (line.operands ? ' ' + line.operands : '');
      row.appendChild(instSpan);

      this.linesEl.appendChild(row);
    }
  }

  destroy(): void {
    if (this.el.parentNode) {
      this.el.parentNode.removeChild(this.el);
    }
  }
}
